import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '../store';

/**
 * Honest feedback for a pair request. The sim acknowledges only "validated and queued" and returns
 * a requestId (the ack command id). Outcomes are looked up by that id in the worker's durable
 * pair-outcome log, so another click, an old alert or a duplicate name can never settle this request:
 *  - conceived: accepted, a pregnancy started
 *  - failed: the attempt ran and she did not conceive
 *  - refused: the sim refused it, with its reason
 *  - moved: the sire was moved to the dam's pen; movement only, the result is still to come
 */
type Phase = { kind: 'idle' } | { kind: 'wait' | 'ok' | 'fail' | 'moved' | 'nope'; text: string };

export function usePairRequest(): { phase: Phase; pending: boolean; send(sireId: string, damId: string, strategy?: string): void; sendAuto(animalId: string): void; clear(): void } {
  const { sim, bridge } = useStore();
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [reqId, setReqId] = useState<string | null>(null);
  const live = useRef(0); // ignores late acks from a request the player has moved on from
  const outcomes = sim.breeding?.pairOutcomes;
  useEffect(() => {
    if (reqId === null || (phase.kind !== 'wait' && phase.kind !== 'moved')) { return; }
    const mine = (outcomes ?? []).filter((o) => o.requestId === reqId);
    const last = mine.at(-1);
    if (!last) { return; }
    if (last.outcome === 'conceived') { setPhase({ kind: 'ok', text: 'Pairing accepted - pregnancy under way.' }); }
    else if (last.outcome === 'failed') { setPhase({ kind: 'nope', text: `The attempt ran, but she did not conceive. Try again later.${last.reason && !/did not conceive/i.test(last.reason) ? ` (${last.reason})` : ''}` }); }
    else if (last.outcome === 'refused') { setPhase({ kind: 'fail', text: `Refused: ${last.reason ?? 'the sim gave no reason.'}` }); }
    else { setPhase({ kind: 'moved', text: 'The sire was moved to the dam\'s pen. That is only movement - still waiting for the pairing result.' }); }
  }, [outcomes, reqId, phase.kind]);
  const pending = phase.kind === 'wait' || phase.kind === 'moved';
  const go = (type: string, payload: unknown): void => {
    const mine = ++live.current;
    setReqId(null);
    setPhase({ kind: 'wait', text: 'Sent. Waiting for the sim to process it.' });
    bridge.sendTracked(type, payload).then((id) => {
      if (live.current !== mine) { return; }
      if (id === null) { setPhase({ kind: 'wait', text: 'Sent. This preview build cannot report the outcome.' }); } else { setReqId(id); }
    }, (err: unknown) => {
      if (live.current !== mine) { return; }
      setPhase({ kind: 'fail', text: `Refused: ${err instanceof Error && err.message ? err.message : 'the sim would not take the request.'}` });
    });
  };
  return {
    pending,
    phase,
    send(sireId, damId, strategy) {
      if (pending) { return; }
      go('breeding.pair.request', strategy && strategy !== 'balanced' ? { sireId, damId, strategy } : { sireId, damId });
    },
    sendAuto(animalId) {
      if (pending) { return; }
      go('breeding.pair.request', { animalId });
    },
    clear() { live.current++; setReqId(null); setPhase({ kind: 'idle' }); },
  };
}

export function PairStatusLine({ phase }: { phase: Phase }) {
  const el = useRef<HTMLDivElement>(null);
  // The line sits under the Pair button, which can be below the fold on a phone: bring each new state into view.
  useEffect(() => { el.current?.scrollIntoView({ block: 'nearest' }); }, [phase.kind, phase.kind === 'idle' ? '' : phase.text]);
  if (phase.kind === 'idle') { return null; }
  const cls = phase.kind === 'fail' ? 'pill pill-red' : phase.kind === 'ok' ? 'pill' : 'pill pill-amber';
  return <div ref={el} class={cls} role="status" style="display:block;margin-top:8px;text-align:center;white-space:normal">{phase.text}</div>;
}
